"use client";

import Link from "next/link";
import {
  Keyword, LifecycleStage,
  STAGE_LABEL, STAGE_EMOJI, STAGE_COLOR, STAGE_BG,
} from "@/types";
import { formatWeeklyChange, formatSearchDirection } from "@/lib/trendDisplay";

/**
 * 통합 키워드 카드 (룰 v4 — 사용자 결정 2026-05-07).
 *
 * 단일 디자인:
 *   [순위] [키워드 + stage 배지 + 시그널] [m30 영향력]
 *
 * 표시값 = 정렬 기준 (m30) — 사용자 혼선 0.
 * 점수 X — 시그널 텍스트로 본질 전달.
 *
 * stage 배지 룰 (contextStage 별):
 *   - "all"        → 모든 stage 라벨 표시 (declining 은 substage 우선)
 *   - "declining"  → substage 만 (🫧 거품 / 📉 일반)
 *   - 그 외 stage  → 배지 X (섹션이 이미 stage 명시)
 */

// 단계의 "natural" 방향 — 단계 정의와 일치 = 노이즈 → 숨김
const NATURAL_DIRECTION: Record<LifecycleStage, string> = {
  emerging: "rising",
  growing: "rising",
  mature: "flat",
  declining: "falling",
};

export interface KeywordCardProps {
  kw: Keyword;
  rank: number;
  contextStage?: LifecycleStage | "all";
  showOverallRank?: boolean;
}

export function KeywordCard({
  kw,
  rank,
  contextStage = "all",
  showOverallRank = false,
}: KeywordCardProps) {
  const m30 = kw.mention_count;
  const stage = kw.lifecycle_stage;
  const searchDir = formatSearchDirection(kw.datalab_direction);
  const stageCtx = stage === "declining" ? "decline" : "surge";
  const weekly = formatWeeklyChange(kw.mention_change_per_day, stageCtx);

  // stage natural 방향 = 노이즈 → 숨김
  const isNaturalDir = stage && kw.datalab_direction === NATURAL_DIRECTION[stage];
  const showSearchDir = !isNaturalDir && searchDir.available;

  // 라이프사이클 배지 텍스트 결정
  // 2026-05-20 (사용자 결정) — "📉 일반 하락" 배지 제거 (default 라 정보 가치 0, 시각 노이즈).
  // bubble 만 특수 배지 (🫧 거품) 유지 — 정말 의미 있는 신호.
  let stageLabel: string | null = null;
  if (stage) {
    if (contextStage === "all") {
      // 전체 탭: bubble 특수 라벨, 그 외는 일반 stage 라벨 (fading 도 "📉 하락기" 로 통합)
      stageLabel =
        stage === "declining" && kw.lifecycle_substage === "bubble"
          ? "🫧 거품 단계"
          : `${STAGE_EMOJI[stage]} ${STAGE_LABEL[stage]}`;
    } else if (contextStage === "declining" && kw.lifecycle_substage === "bubble") {
      // 하락기 탭 안 — bubble 만 표시. fading 은 default 라 배지 없음
      stageLabel = "🫧 거품 단계";
    }
  }

  return (
    <Link href={`/trend/${kw.id}`}>
      {/* 컴팩트 카드 (사용자 결정 2026-05-07): p-4 → p-2.5, gap-3 → gap-2.5, mt-1 → mt-0.5 */}
      <div className="bg-bg-card rounded-xl px-3 py-2.5 border border-border hover:border-primary/40 transition-colors cursor-pointer grid grid-cols-[2rem_1fr_auto] items-center gap-2.5">
        {/* 순위 */}
        <span className="text-xs text-text-sub font-semibold whitespace-nowrap">
          {rank}
          {showOverallRank && kw.overall_rank && (
            <span className="text-[9px] text-text-sub/60 block leading-tight">
              전체 {kw.overall_rank}
            </span>
          )}
        </span>

        {/* 키워드 + 보조 정보 */}
        <div className="min-w-0">
          <p className="text-sm font-bold text-text truncate flex items-center gap-1.5">
            <span className="truncate">{kw.keyword}</span>
            {kw.is_new && (
              <span className="text-[10px] font-bold px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-400 shrink-0">
                🆕 신규
              </span>
            )}
          </p>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap leading-tight">
            {stageLabel && (
              <span
                className="text-2xs font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                style={{
                  color: stage ? STAGE_COLOR[stage] : undefined,
                  background: stage ? STAGE_BG[stage] : undefined,
                }}
              >
                {stageLabel}
              </span>
            )}
            {/* 마이그 087 (2026-05-20) — 시즌 배지 2종:
                 annual  🌸 매해 N월 시즌 (3년 이상 같은 월 반복)
                 one_off ✨ 2026년 N월 폭발 (단발성)
                 — peak/avg 비율 약하거나 데이터 부족이면 칩 미표시 */}
            {kw.seasonal_type === "annual" && (
              <span
                className="text-2xs font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap bg-rose-500/15 text-rose-400"
                title={kw.seasonal_peak_months ? `매해 ${kw.seasonal_peak_months}월 피크 (3년 이상 반복)` : "매해 반복 시즌"}
              >
                🌸 매해{kw.seasonal_peak_months ? ` ${kw.seasonal_peak_months}월` : ""}
              </span>
            )}
            {kw.seasonal_type === "one_off" && kw.seasonal_peak_period && (() => {
              // 2026-05-25 시제 정정 — peak 가 올해/작년/그 이전 구분.
              const [y, m] = kw.seasonal_peak_period.split("-");
              const peakYear = parseInt(y, 10);
              const peakMonth = parseInt(m, 10);
              const now = new Date();
              const thisYear = now.getFullYear();
              let label: string;
              let titleSuffix: string;
              if (peakYear === thisYear) {
                label = `이번 ${peakMonth}월`;
                titleSuffix = "올해 단발 폭발 (반복 미확인)";
              } else if (peakYear === thisYear - 1) {
                label = `지난 ${peakMonth}월`;
                titleSuffix = "작년 단발 폭발 (반복 미확인 — 올해 재현 가능성)";
              } else {
                label = `${peakYear}년 ${peakMonth}월`;
                titleSuffix = "단발 폭발 (반복 미확인)";
              }
              return (
                <span
                  className="text-2xs font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap bg-amber-500/15 text-amber-400"
                  title={`${label} ${titleSuffix}`}
                >
                  ✨ {label}
                </span>
              );
            })()}
            {showSearchDir && (
              <span
                className="text-2xs font-semibold whitespace-nowrap"
                style={{ color: searchDir.color }}
              >
                {searchDir.shortLabel}
              </span>
            )}
            {weekly && (
              <span
                className="text-2xs font-bold whitespace-nowrap"
                style={{ color: weekly.color }}
                title="최근 7일 평균 vs 30일 평균 일차 × 7 (가속·감속 추정)"
              >
                {weekly.text}
              </span>
            )}
          </div>
        </div>

        {/* m30 영향력 — 정렬 기준과 일치. "30일" 라벨은 행에서 제거 (페이지 헤더 1회만) */}
        <div className="text-right shrink-0">
          <p className="text-sm font-black text-text leading-none">{m30}건</p>
        </div>
      </div>
    </Link>
  );
}
